import {
  Table as MaterialTable,
  TableBody,
  TableContainer,
  TablePagination,
} from "@material-ui/core";
import { differenceWith, isEmpty, noop } from "lodash";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Pageable } from "../../services/pageable";
import { colours } from "../../styles/colours";
import { Icon } from "../icons";
import { StyledTable } from "./base";
import { TableFilter } from "./Filter";
import { TableHead, TableHeaders } from "./Head";

export const Container = styled(TableContainer)`
  background: ${colours.white};
  color: ${colours.darkBlueGrey};
`;

export const BooleanField = styled(Icon).attrs({ type: "circle" })`
  display: inline-block;
`;

export type RenderFields<DataShape> = Partial<
  Record<keyof DataShape, React.FunctionComponent<DataShape>>
>;

export type TableProps<
  Shape,
  Headers extends keyof Shape,
  Unsortable extends Headers = never
> = {
  readonly head: TableHeaders<Headers>;
  readonly data: Array<Shape>;
  readonly renderFields?: Partial<
    Record<Headers, React.FunctionComponent<Shape>>
  >;
  readonly filters?: Partial<Record<Headers, React.ReactNode>>;
  readonly hide?: Array<Headers>;
  readonly pagination?: Pageable.Response.T;
  readonly onPaginationChange?: (
    x: Pageable.T<Exclude<Headers, Unsortable>>
  ) => void;
  readonly pageableState?: Pageable.T<Exclude<Headers, Unsortable>>;
  readonly unsortable?: Array<Unsortable>;
  readonly rowOnClick?: (s: Shape) => void;
  readonly cellOnClick?: Partial<Record<Headers, (s: Shape) => void>>;
};

export function TableCell<Shape, Headers extends keyof Shape>({
  row,
  renderFields,
  name,
  centered,
  hiddenMobile,
  cellOnClick,
}: {
  renderFields?: Partial<Record<Headers, React.FunctionComponent<Shape>>>;
  row: Shape;
  name: Headers;
  hiddenMobile?: boolean;
  centered?: boolean;
  cellOnClick?: (s: Shape) => void;
}) {
  const component = useMemo(() => renderFields?.[name], [renderFields, name]);

  const rendered = useMemo(() => {
    if (component !== undefined) {
      return React.createElement(component, row);
    } else if (typeof row[name] === "boolean") {
      return <BooleanField colour1={row[name] ? colours.green : colours.red} />;
    } else {
      return row[name];
    }
  }, [component, row, name]);

  const onClick: (e: Event) => void = useCallback(
    (e: Event) => {
      if (cellOnClick) {
        e.stopPropagation();
        cellOnClick(row);
      }
    },
    [cellOnClick, row]
  );

  return useMemo(
    () => (
      <StyledTable.Cell
        key={name.toString()}
        align={centered ? "center" : "left"}
        hiddenMobile={hiddenMobile}
        onClick={onClick}
      >
        {rendered}
      </StyledTable.Cell>
    ),
    [rendered, name, onClick, hiddenMobile, centered]
  );
}

export function TableRow<Shape, Headers extends keyof Shape = keyof Shape>({
  filteredHead,
  filters,
  rowOnClick,
  row,
  renderFields,
  cellOnClick,
}: {
  filteredHead: Array<{
    name: Headers;
    title: string;
    centered?: boolean | undefined;
    hiddenMobile?: boolean | undefined;
  }>;
  filters: Partial<Record<Headers, React.ReactNode>>;
  rowOnClick?: (s: Shape) => void;
  row: Shape;
  renderFields?: Partial<Record<Headers, React.FunctionComponent<Shape>>>;
  cellOnClick?: Partial<Record<Headers, (s: Shape) => void>>;
}) {
  const onClick = useCallback(
    () => (rowOnClick ? rowOnClick(row) : undefined),
    [row]
  );

  const cells = useMemo(
    () =>
      filteredHead.map(({ name, centered, hiddenMobile }) => (
        <TableCell
          key={String(name)}
          row={row}
          renderFields={renderFields}
          name={name}
          centered={centered}
          hiddenMobile={hiddenMobile}
          cellOnClick={cellOnClick?.[name]}
        />
      )),
    [filteredHead, renderFields, row, cellOnClick]
  );

  return useMemo(
    () => (
      <StyledTable.Row even={!isEmpty(filters)} onClick={onClick}>
        {cells}
      </StyledTable.Row>
    ),
    [cells, filters, onClick]
  );
}

export function Table<
  Shape,
  Headers extends keyof Shape = keyof Shape,
  Unsortable extends Headers = never
>({
  head,
  data,
  renderFields = {},
  hide = [],
  filters = {},
  pagination,
  onPaginationChange = noop,
  pageableState,
  rowOnClick,
  cellOnClick = {},
  unsortable = [],
}: TableProps<Shape, Headers, Unsortable>) {
  const filteredHead = useMemo(
    () => differenceWith(head, hide, (x, y) => x.name === y),
    [head, hide]
  );
  if (filteredHead.length < 1) throw new Error("no table fields specified");

  const onSortChange = useCallback(
    (x) =>
      onPaginationChange({
        sorting: x,
        pagination: {
          page: 0,
          size: pagination?.pageSize || 20,
        },
        filters: pageableState?.filters,
      }),
    [onPaginationChange, pageableState, pagination]
  );

  const rows = useMemo(
    () =>
      data.map((row, index) => (
        <TableRow<Shape, Headers>
          key={index}
          filteredHead={filteredHead}
          row={row}
          filters={filters}
          cellOnClick={cellOnClick}
          rowOnClick={rowOnClick}
          renderFields={renderFields}
        />
      )),
    [data, filteredHead, filters, cellOnClick, rowOnClick, renderFields]
  );

  const paginationComponent = useMemo(
    () =>
      pagination ? (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={pagination.totalElements}
          rowsPerPage={pagination.pageSize}
          page={pagination.pageNumber}
          onPageChange={(_, y) =>
            onPaginationChange({
              pagination: { page: y, size: pagination.pageSize },
              filters: pageableState?.filters,
              sorting: pageableState?.sorting,
            })
          }
          onRowsPerPageChange={(x) =>
            onPaginationChange({
              pagination: { page: 0, size: parseInt(x.target.value, 10) },
              filters: pageableState?.filters,
              sorting: pageableState?.sorting,
            })
          }
        />
      ) : null,
    [pagination, onPaginationChange, pageableState]
  );

  return useMemo(
    () => (
      <Container>
        <MaterialTable
          aria-labelledby="tableTitle"
          size="medium"
          aria-label="enhanced table"
        >
          <TableHead<Headers, Unsortable>
            unsortable={unsortable}
            fields={filteredHead}
            sort={pageableState && pageableState.sorting}
            onSortChange={onSortChange}
          />
          <TableBody>
            <TableFilter fields={filteredHead} filters={filters} />
            {rows}
          </TableBody>
        </MaterialTable>
        {paginationComponent}
      </Container>
    ),
    [
      unsortable,
      filteredHead,
      filters,
      pageableState,
      paginationComponent,
      rows,
    ]
  );
}
